<template>
  <div class="container">
    <div class="content-wrapper">
      <!-- 步骤条 -->
      <div class="steps-wrapper">
        <div class="steps">
          <div
              v-for="(step, index) in steps"
              :key="index"
              :class="['step-item', { 'step-item-active': currentStep === index, 'step-item-completed': currentStep > index }]"
          >
            <div class="step-item-container">
              <div class="step-item-tail"></div>
              <div class="step-item-icon">
                <span v-if="currentStep > index" class="step-icon-check">
                  <el-icon><CircleCheck/></el-icon>
                </span>
                <span v-else class="step-icon-number">{{ index + 1 }}</span>
              </div>
              <div class="step-item-content">
                <div class="step-item-title">{{ step.title }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 第一步：选择产品卡片 -->
      <div v-if="currentStep === 0" class="card card-step1">
        <div class="card-body">
          <div class="product-selection">
            <!-- 产品列表（图形化展示） -->
            <div class="product-grid">
              <div
                  v-for="product in productList"
                  :key="product.model_id"
                  :class="['product-card', { 'product-card-selected': formData.service_device && formData.service_device.model_id === product.model_id }]"
                  @click="selectProduct(product)"
              >
                <div class="product-image-wrapper">
                  <img
                      v-if="product.model_url"
                      :src="product.model_url"
                      :alt="product.brand_name + '-' + product.model_name"
                      class="product-image"
                  />
                  <div v-else class="product-image-placeholder">
                    <el-icon :size="40">
                      <Picture/>
                    </el-icon>
                  </div>
                </div>
                <div class="product-info">
                  <div class="product-brand">{{ product.brand_name }}</div>
                  <div class="product-model">{{ product.model_name }}</div>
                </div>
                <div v-if="formData.service_device && formData.service_device.model_id === product.model_id"
                     class="product-selected-icon">
                  <el-icon>
                    <CircleCheck/>
                  </el-icon>
                </div>
              </div>
            </div>

            <!-- 下一步按钮 -->
            <div class="form-actions">
              <el-button type="primary" size="large" @click="nextStep" :disabled="!formData.service_device">
                下一步
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 第二步：填写相关信息卡片 -->
      <div v-if="currentStep === 1" class="card card-step2">
        <div class="card-body">
          <div class="rental-form-step">
            <!-- 商品详情主体 -->
            <div class="product-main">
              <!-- 左侧：图片和详情标签页 -->
              <div class="product-left-section">
                <!-- 图片 -->
                <div class="product-image-box">
                  <div class="main-image">
                    <img
                        v-if="formData.service_device && formData.service_device.model_url"
                        :src="formData.service_device.model_url"
                        :alt="formData.service_device.brand_name + '-' + formData.service_device.model_name"
                    />
                    <img
                        v-else
                        src="https://wiseagent.oss-cn-shanghai.aliyuncs.com/images/icon/CP500.jpg"
                        alt="CP500 证卡打印机"
                    />
                  </div>
                </div>

                <!-- 详情标签页 -->
                <div class="detail-tabs">
                  <div class="tab-header">
                    <div
                        :class="['tab-item', { 'active': activeTab === 'intro' }]"
                        @click="activeTab = 'intro'"
                    >
                      商品详情
                    </div>
                    <div
                        :class="['tab-item', { 'active': activeTab === 'specs' }]"
                        @click="activeTab = 'specs'"
                    >
                      产品参数
                    </div>
                    <div
                        :class="['tab-item', { 'active': activeTab === 'service' }]"
                        @click="activeTab = 'service'"
                    >
                      服务说明
                    </div>
                  </div>

                  <!-- 商品详情 -->
                  <div v-show="activeTab === 'intro'" class="tab-content active">
                    <div class="detail-section">
                      <h3>产品概述</h3>
                      <p>CP500 是由 GET 集团与日本凸版印刷公司（Toppan Printing Company）联合打造的全球最好桌面式 600dpi
                        颜料墨水再转印证卡打印机。凭借 GET 集团 25 年来为全球政府提供高防伪护照及身份证件解决方案的深厚经验，以及凸版印刷公司世界领先的防伪印刷技术，CP500
                        将政府级安全标准引入商业市场，为需要高安全性和高品质证卡发行的机构提供卓越选择。</p>
                    </div>

                    <div class="detail-section">
                      <h3>核心优势</h3>

                      <div class="advantage-item">
                        <h4>(1) 革命性的 600dpi 颜料墨水技术</h4>
                        <ul>
                          <li><strong>超高分辨率：</strong>逐点颜料墨水技术实现 600dpi 物理分辨率，图像细节清晰度冠绝全球，肉眼即可轻松鉴别真伪；
                          </li>
                          <li><strong>微缩文字防伪：</strong>支持打印肉眼难以辨认的微缩文字，这是其他打印机无法企及的安全特性；
                          </li>
                          <li><strong>超强耐候性：</strong>CP500 打印的证卡具备业界最强的抗紫外线老化能力，确保卡片颜色持久鲜艳、表面完整不褪色，有效延长证卡使用寿命。
                          </li>
                        </ul>
                      </div>

                      <div class="advantage-item">
                        <h4>(2) 独特的再转印打印工艺</h4>
                        <ul>
                          <li><strong>边到边完美打印：</strong>真正实现无边距全幅面打印，兼顾美观与专业；</li>
                          <li><strong>防篡改覆膜：</strong>打印后自动添加防损改覆膜，有效防止图像或数据被恶意篡改；</li>
                          <li><strong>灵活防伪集成：</strong>可轻松嵌入全息图、条形码、微缩文字等多重防伪元素，构筑立体化防伪体系；
                          </li>
                          <li><strong>材质兼容性强：</strong>支持 PVC、PET-G、PC、Teslin 复合材料等多种卡片基材，适配不同安全等级需求。
                          </li>
                        </ul>
                      </div>

                      <div class="advantage-item">
                        <h4>(3) 极致性能与效率</h4>
                        <ul>
                          <li><strong>高速输出：</strong>单面全彩打印仅需 25 秒，每小时可生产 144 张证卡（CMYKP
                            模式），大幅提升制证效率；
                          </li>
                          <li><strong>大容量供卡系统：</strong>300 张进卡容量+250 张出卡容量，满足批量连续作业需求，可选配锁止装置保障空白卡安全。
                          </li>
                        </ul>
                      </div>

                      <div class="advantage-item">
                        <h4>(4) 灵活的扩展与集成能力</h4>
                        <ul>
                          <li><strong>可升级编码模块：</strong>支持现场加装磁条编码器（ISO7811）、接触式智能卡编码器（ISO7816）、MIFARE
                            非接触式编码器（ISO14443）等多种编码功能；
                          </li>
                          <li><strong>标配双接口：</strong>USB 2.0（高速）与以太网接口双标配，确保与任何 IT 环境轻松集成；
                          </li>
                          <li><strong>完整 SDK 支持：</strong>GET 集团提供经验丰富的集成团队与完善的开发工具包，可与现有安防系统、身份管理平台无缝衔接。
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div class="detail-section">
                      <h3>租赁模式介绍</h3>

                      <div class="advantage-item">
                        <h4>1、模式 A：基础租赁（适合稳定需求）</h4>
                        <ul>
                          <li><strong>(1)月租：</strong>800 元/月/台（不含耗材）；</li>
                          <li><strong>(2)包月：</strong>1500 元/月/台，包含一卷色带，一张清洁卡；</li>
                          <li><strong>(3)耗材费用：</strong>色带 1500 元/卷（约制 500 张），清洁卡¥50 元/张（每制卡 500
                            张需要清洁一次）；
                          </li>
                          <li><strong>(4)押金：</strong>5000 元；</li>
                        </ul>
                      </div>

                      <div class="advantage-item">
                        <h4>2、模式 B：按量计费（适合波动需求）</h4>
                        <ul>
                          <li><strong>(1)起付门槛：</strong>500 张/台，1500 元/台，含耗材；</li>
                          <li><strong>(2)阶梯定价</strong>
                            <ul style="margin-top: 8px; padding-left: 20px;">
                              <li>制卡低于 500 张：按 500 张计算；</li>
                              <li>制卡 500 张以上：2.8 元/张；</li>
                            </ul>
                          </li>
                          <li><strong>(3)押金：</strong>5000 元；</li>
                        </ul>
                      </div>

                      <div class="advantage-item">
                        <h4>3、模式 C：临时租赁</h4>
                        <ul>
                          <li><strong>(1)租赁时间：</strong>3-7 天不等；</li>
                          <li><strong>(2)设备占用费：</strong>300 元/天；</li>
                          <li><strong>(3)制卡单独收费：</strong>10 元/张；</li>
                          <li><strong>(4)押金：</strong>5000 元（政府部门等可免押金）；</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- 产品参数 -->
                  <div v-show="activeTab === 'specs'" class="tab-content">
                    <div class="detail-section">
                      <h3>技术参数</h3>
                      <table style="width: 100%; border-collapse: collapse;">
                        <tr style="border-bottom: 1px solid #e5e5e5;">
                          <td style="padding: 12px; width: 200px; color: #666; background: #fafafa;">打印分辨率</td>
                          <td style="padding: 12px; color: #333;">600dpi</td>
                        </tr>
                        <tr style="border-bottom: 1px solid #e5e5e5;">
                          <td style="padding: 12px; color: #666; background: #fafafa;">打印速度</td>
                          <td style="padding: 12px; color: #333;">25秒/张（单面全彩）</td>
                        </tr>
                        <tr style="border-bottom: 1px solid #e5e5e5;">
                          <td style="padding: 12px; color: #666; background: #fafafa;">生产能力</td>
                          <td style="padding: 12px; color: #333;">144张/小时</td>
                        </tr>
                        <tr style="border-bottom: 1px solid #e5e5e5;">
                          <td style="padding: 12px; color: #666; background: #fafafa;">进卡容量</td>
                          <td style="padding: 12px; color: #333;">300张</td>
                        </tr>
                        <tr style="border-bottom: 1px solid #e5e5e5;">
                          <td style="padding: 12px; color: #666; background: #fafafa;">出卡容量</td>
                          <td style="padding: 12px; color: #333;">250张</td>
                        </tr>
                        <tr style="border-bottom: 1px solid #e5e5e5;">
                          <td style="padding: 12px; color: #666; background: #fafafa;">接口类型</td>
                          <td style="padding: 12px; color: #333;">USB 2.0、以太网</td>
                        </tr>
                        <tr style="border-bottom: 1px solid #e5e5e5;">
                          <td style="padding: 12px; color: #666; background: #fafafa;">支持材质</td>
                          <td style="padding: 12px; color: #333;">PVC、PET-G、PC、Teslin 复合材料</td>
                        </tr>
                      </table>
                    </div>
                  </div>

                  <!-- 服务说明 -->
                  <div v-show="activeTab === 'service'" class="tab-content">
                    <div class="detail-section">
                      <h3>租赁服务说明</h3>
                      <div class="advantage-item">
                        <h4>租赁流程</h4>
                        <ul>
                          <li>1. 选择租赁模式并填写申请表单</li>
                          <li>2. 我们会在1-2个工作日内与您联系</li>
                          <li>3. 确认租赁信息并签署合同</li>
                          <li>4. 支付押金后安排设备发货</li>
                          <li>5. 设备到达后提供安装指导和技术支持</li>
                        </ul>
                      </div>
                      <div class="advantage-item">
                        <h4>服务保障</h4>
                        <ul>
                          <li>专业的技术支持团队，7×24小时在线服务</li>
                          <li>设备故障快速响应，提供备用设备保障</li>
                          <li>定期设备维护和耗材供应服务</li>
                          <li>灵活的租赁方案，可根据需求调整</li>
                        </ul>
                      </div>
                      <div class="advantage-item">
                        <h4>注意事项</h4>
                        <ul>
                          <li>租赁期间需妥善保管设备，如有损坏需按价赔偿</li>
                          <li>政府部门等机构可申请免押金服务</li>
                          <li>租赁期满可续租或购买设备</li>
                          <li>具体租赁条款以合同为准</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 右侧信息（固定定位） -->
              <div class="product-info-box">
                <h1 class="product-title">
                  {{
                    formData.service_device ? (formData.service_device.brand_name + ' ' + formData.service_device.model_name) : 'CP500 证卡打印机'
                  }}
                </h1>
                <p class="product-subtitle">世界领先的桌面式安全证卡打印解决方案</p>

                <!-- 价格区域 -->
                <div class="price-section">
                  <div class="price-label">租赁价格</div>
                  <div class="price-main">
                    <span>{{ mainPrice }}</span>
                    <span class="price-unit">{{ priceUnit }}</span>
                  </div>
                  <div class="price-desc">{{ priceDesc }}</div>
                </div>

                <!-- 服务保障 -->
                <div class="service-tags">
                  <div class="service-tag">专业服务</div>
                  <div class="service-tag">快速响应</div>
                  <div class="service-tag">设备保障</div>
                  <div class="service-tag">技术支持</div>
                </div>

                <!-- 规格选择（租赁模式） -->
                <div class="spec-section">
                  <div class="spec-label">选择租赁模式</div>

                  <!-- 租赁模式列表（扁平化显示，有子模式的直接显示子模式） -->
                  <div class="spec-options">
                    <div v-if="flattenedRentalModeList.length === 0" class="spec-empty">
                      正在加载租赁模式...
                    </div>
                    <div
                        v-for="mode in flattenedRentalModeList"
                        :key="mode.id"
                        :class="['spec-option', { 'selected': selectedRentalMode && selectedRentalMode.id === mode.id }]"
                        @click="selectRentalMode(mode)"
                    >
                      <div class="spec-name">{{ mode.mode_name }}</div>
                      <div class="spec-price">
                        <span v-if="getModePrice(mode)">{{ getModePrice(mode) }}</span>
                        <span v-else>--</span>
                      </div>
                      <div class="spec-desc">{{ mode.description }}</div>
                      <div class="spec-details">
                        <div class="spec-details-item" v-if="mode.mode_code === 'MODE_B'">
                          <span>起付门槛：</span>
                          <span>{{ getModeThreshold(mode) }}</span>
                        </div>
                        <div class="spec-details-item" v-if="mode.mode_code === 'MODE_C'">
                          <span>制卡费用：</span>
                          <span>{{ getCardPrintPrice(mode) }}</span>
                        </div>
                        <div class="spec-details-item">
                          <span>押金：</span>
                          <span>¥{{ mode.deposit_amount }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 操作按钮 -->
                <div class="action-section">
                  <button
                      class="rent-btn"
                      :disabled="!canSubmitRental"
                      @click="openForm"
                  >
                    立即申请
                  </button>
                </div>
              </div>
            </div>

            <!-- 操作按钮 -->
            <div class="form-actions">
              <el-button size="large" @click="prevStep">上一步</el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 表单弹窗 -->
      <el-dialog
          v-model="showFormModal"
          width="800px"
          :close-on-click-modal="false"
          @close="closeForm"
      >
        <template #header>
          <div class="form-header">
            <h2>{{ selectedModeText || '填写租赁申请' }}</h2>
          </div>
        </template>

        <el-form ref="rentalFormRef" :model="rentalFormData" :rules="rentalFormRules" label-width="140px">
          <el-form-item label="选择地址" prop="selected_address_id" required>
            <div class="address-list">
              <div
                  v-for="address in rentalAddressList"
                  :key="address.id"
                  :class="['address-item', { 'address-item-selected': rentalFormData.selected_address_id === address.id }]"
                  @click="handleRentalAddressSelect(address)"
              >
                <div class="address-item-header">
                  <div class="address-item-title">
                    <span class="address-text">{{ address.detail_address }}{{ address.building_number || '' }}</span>
                    <el-tag v-if="address.is_default" size="small" type="success" style="margin-left: 8px;">默认
                    </el-tag>
                  </div>
                  <div v-if="rentalFormData.selected_address_id === address.id" class="address-item-check">
                    <el-icon>
                      <CircleCheck/>
                    </el-icon>
                  </div>
                </div>
                <div class="address-item-info">
                  <div class="address-info-item">
                    <span class="address-info-label">联系人：</span>
                    <span class="address-info-value">{{ address.contact_name }}</span>
                  </div>
                  <div class="address-info-item">
                    <span class="address-info-label">联系电话：</span>
                    <span class="address-info-value">{{ address.telephone }}</span>
                  </div>
                </div>
              </div>
              <div v-if="rentalAddressList.length === 0" class="address-empty">
                暂无地址信息
              </div>
            </div>
          </el-form-item>

          <el-form-item label="数量" prop="estimate_time_num" required>
            <div style="display: flex; align-items: center; width: 300px;">
              <el-input-number
                  v-model="rentalFormData.estimate_time_num"
                  :min="1"
                  style="flex: 1;"
              />
              <span style="margin-left: 10px; color: #666; font-size: 14px;">
                  {{
                  selectedRentalMode ? (selectedRentalMode.mode_code === 'MODE_B' ? '张' : selectedRentalMode.mode_code === 'MODE_C' ? '天' : '月') : ''
                }}
                </span>
            </div>
          </el-form-item>

          <el-form-item label="用途说明" prop="rental_purpose">
            <el-input
                v-model="rentalFormData.rental_purpose"
                type="textarea"
                :rows="3"
                placeholder="请描述租赁用途（选填）"
            />
          </el-form-item>
          <el-form-item label="身份证正面" prop="legal_id_front_img" required>
            <UploadMedia v-model="rentalFormData.legalIdFrontList" number="1" accept="image"/>
          </el-form-item>

          <el-form-item label="身份证反面" prop="legal_id_back_img" required>
            <UploadMedia v-model="rentalFormData.legalIdBackList" number="1" accept="image"/>
          </el-form-item>

          <el-form-item>
            <el-checkbox v-model="rentalFormData.is_deposit_free">
              是否免押金（政府部门等可申请免押金）
            </el-checkbox>
          </el-form-item>
        </el-form>

        <template #footer>
          <div class="dialog-footer">
            <el-button @click="closeForm">取消</el-button>
            <el-button type="primary" @click="submitRentalForm" :loading="isSubmittingRental">
              提交申请
            </el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<style scoped>
.container {
  display: flex;
  background: #f5f6fa;
}

.content-wrapper {
  margin: auto;
  width: 100%;
  max-width: 1600px;
  padding: 1.5rem;
  box-sizing: border-box;
}

.page-header {
  text-align: center;
  margin-bottom: 2rem;
}

.page-header h1 {
  font-size: 2rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

/* 步骤条 */
.steps-wrapper {
  margin-bottom: 2rem;
}

.steps {
  display: flex;
  justify-content: center;
  position: relative;
}

.step-item {
  flex: 1;
  position: relative;
}

.step-item-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-item-tail {
  position: absolute;
  top: 1rem;
  left: 50%;
  width: 100%;
  height: 2px;
  background: #e8e8e8;
  z-index: 0;
}

.step-item:last-child .step-item-tail {
  display: none;
}

.step-item-completed .step-item-tail {
  background: #1890ff;
}

.step-item-icon {
  position: relative;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: all 0.3s;
}

.step-item-active .step-item-icon {
  border-color: #1890ff;
  background: #1890ff;
  color: #fff;
}

.step-item-completed .step-item-icon {
  border-color: #1890ff;
  background: #1890ff;
  color: #fff;
}

.step-icon-number {
  font-size: 0.875rem;
  font-weight: 600;
  color: #999;
}

.step-item-active .step-icon-number {
  color: #fff;
}

.step-icon-check {
  color: #fff;
}

.step-item-content {
  margin-top: 0.5rem;
}

.step-item-title {
  font-size: 0.875rem;
  color: #999;
  transition: color 0.3s;
}

.step-item-active .step-item-title {
  color: #1890ff;
  font-weight: 600;
}

.step-item-completed .step-item-title {
  color: #333;
}

/* 卡片 */
/* 卡片基础样式 */
.card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(230, 230, 230, 0.5);
  margin: 0 auto;
  box-sizing: border-box;
}

/* 第一步卡片：保持原有宽度 */
.card-step1 {
  width: 52rem;
  max-width: 52rem;
}

/* 第二步卡片：更宽的宽度 */
.card-step2 {
  width: 100%;
  max-width: 1600px;
}

.card-body {
  padding: 2rem 2.5rem;
  box-sizing: border-box;
}

/* 确保第二步内容不会溢出 */
.rental-form-step * {
  box-sizing: border-box;
}

/* 表单 */
.order-form {
  width: 100%;
}

.form-step {
  width: 100%;
}

/* 第二步布局：左右 40% / 60% 弹性盒子 */
.fault-step-layout {
  display: flex;
  gap: 1.5rem;
}

.fault-left {
  flex: 0 0 40%;
  max-width: 40%;
}

.fault-right {
  flex: 1;
}

/* 第三步：联系方式表单居中 */
.contact-step-layout {
  display: flex;
  justify-content: center;
}

.contact-form-inner {
  width: 100%;
  max-width: 420px;
}

/* 订单摘要 */
.order-summary {
  width: 100%;
}

.summary-table {
  margin-bottom: 2rem;
}

.summary-row {
  display: flex;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  width: 120px;
  font-weight: 500;
  color: #666;
  flex-shrink: 0;
}

.summary-value {
  flex: 1;
  color: #333;
}

.summary-value-full {
  flex: 1 1 100%;
}

.summary-media-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.summary-media {
  width: 80px;
  height: 80px;
  object-fit: cover;
}

.summary-media:hover {
  cursor: pointer;
}

/* 操作按钮 */
.form-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.swiper-area {
  margin-left: 0;
  width: 240px;
  height: 80px;
}

/* 图形化产品选择界面 */
.product-selection {
  width: 100%;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.product-category-tabs {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  flex: 1;
}

.category-tab {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9375rem;
  border: 2px solid transparent;
}

.category-tab:hover {
  background: #e6f7ff;
  color: #1890ff;
}

.category-tab-active {
  background: #1890ff;
  color: #fff;
  border-color: #1890ff;
}

.product-type-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.type-tab {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background: #fafafa;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.875rem;
  border: 1px solid #e8e8e8;
}

.type-tab:hover {
  background: #e6f7ff;
  color: #1890ff;
  border-color: #1890ff;
}

.type-tab-active {
  background: #e6f7ff;
  color: #1890ff;
  border-color: #1890ff;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.product-card {
  position: relative;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.product-card:hover {
  border-color: #1890ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  transform: translateY(-2px);
}

.product-card-selected {
  border-color: #1890ff;
  background: #e6f7ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
}

.product-image-wrapper {
  width: 100%;
  height: 120px;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border-radius: 8px;
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  background: #f5f5f5;
}

.product-info {
  width: 100%;
}

.product-brand {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.product-model {
  font-size: 0.75rem;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-selected-icon {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  background: #1890ff;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}


/* 已选择产品回显 */
.selected-product-display {
  margin-bottom: 0;
  height: 100%;
}

.selected-product-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #eef0f4;
  height: 100%;
}

.selected-product-image {
  width: 100%;
  max-width: 360px;
  height: 260px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.selected-product-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.selected-product-info {
  width: 100%;
  text-align: center;
}

.selected-product-title {
  font-size: 0.75rem;
  color: #999;
  margin-bottom: 0.25rem;
}

.selected-product-name {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.25rem;
}

.selected-product-number {
  font-size: 0.875rem;
  color: #666;
}

/* 第二步：租赁表单样式 */
.rental-form-step {
  width: 100%;
}

.product-main {
  display: flex;
  padding: 0;
  margin-bottom: 20px;
  gap: 30px;
  position: relative;
}

.product-left-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.product-image-box {
  width: 100%;
  display: flex;
  align-items: flex-start;
}

.main-image {
  width: 100%;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  background: #fafafa;
  padding: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  box-sizing: border-box;
}

.main-image img {
  max-width: 100%;
  max-height: 100%;
  height: auto;
  object-fit: contain;
  vertical-align: middle;
}

.product-info-box {
  position: sticky;
  top: 20px;
  width: 420px;
  flex-shrink: 0;
  height: fit-content;
  max-height: calc(100vh - 40px);
}

.product-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
}

.product-subtitle {
  font-size: 14px;
  color: #999;
  margin-bottom: 20px;
}

.price-section {
  background: #fff3f0;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
}

.price-label {
  font-size: 14px;
  color: #999;
  margin-bottom: 5px;
}

.price-main {
  font-size: 36px;
  color: #ff5000;
  font-weight: 700;
  font-family: Arial;
}

.price-unit {
  font-size: 18px;
  margin-left: 5px;
}

.price-desc {
  font-size: 12px;
  color: #999;
  margin-top: 5px;
}

.service-tags {
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e5e5;
}

.service-tag {
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
}

.service-tag:before {
  content: "✓";
  color: #ff5000;
  margin-right: 5px;
  font-weight: bold;
}

.spec-section {
  margin-bottom: 25px;
}

.spec-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.spec-label:before {
  content: "*";
  color: #ff5000;
  margin-right: 5px;
}

.spec-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.spec-empty {
  width: 100%;
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

.spec-option {
  flex: 1;
  min-width: 200px;
  max-width: calc(33.333% - 7px);
  padding: 15px 20px;
  border: 2px solid #e5e5e5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
  position: relative;
  box-sizing: border-box;
}

.spec-option:hover {
  border-color: #ff5000;
}

.spec-option.selected {
  border-color: #ff5000;
  background: #fff3f0;
}

.spec-option.selected:after {
  content: "✓";
  position: absolute;
  top: 5px;
  right: 10px;
  color: #ff5000;
  font-weight: bold;
  font-size: 18px;
}

.spec-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.spec-price {
  font-size: 20px;
  color: #ff5000;
  font-weight: 700;
  margin-bottom: 5px;
}

.spec-desc {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

.spec-details {
  font-size: 12px;
  color: #666;
  line-height: 1.6;
}

.spec-details-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.sub-mode-section {
  margin-top: 15px;
}

.sub-mode-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  font-weight: 500;
}

.back-to-modes {
  margin-top: 15px;
  text-align: left;
}

.action-section {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e5e5e5;
}

.rent-btn {
  width: 100%;
  height: 50px;
  background: #ff5000;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
  margin-bottom: 10px;
}

.rent-btn:hover {
  background: #ff6600;
}

.rent-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.consult-btn {
  width: 100%;
  height: 50px;
  background: white;
  color: #ff5000;
  border: 2px solid #ff5000;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.consult-btn:hover {
  background: #fff3f0;
}

.detail-tabs {
  width: 100%;
}

.tab-header {
  display: flex;
  border-bottom: 2px solid #e5e5e5;
  padding: 0 20px;
}

.tab-item {
  padding: 15px 30px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: all 0.3s;
}

.tab-item:hover {
  color: #ff5000;
}

.tab-item.active {
  color: #ff5000;
  border-bottom-color: #ff5000;
  font-weight: 600;
}

.tab-content {
  padding: 30px;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h3 {
  font-size: 18px;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e5e5;
}

.detail-section p {
  font-size: 14px;
  color: #666;
  line-height: 2;
  margin-bottom: 15px;
}

.advantage-list {
  list-style: none;
}

.advantage-item {
  margin-bottom: 20px;
  padding: 15px;
  background: #fafafa;
  border-left: 3px solid #ff5000;
}

.advantage-item h4 {
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
}

.advantage-item ul {
  list-style: none;
  padding-left: 0;
}

.advantage-item ul li {
  font-size: 14px;
  color: #666;
  line-height: 2;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.advantage-item ul li:before {
  content: "•";
  position: absolute;
  left: 0;
  color: #ff5000;
  font-weight: bold;
}

.form-header {
  margin-bottom: 0;
  padding-bottom: 15px;
  border-bottom: 1px solid #e5e5e5;
}

.form-header h2 {
  font-size: 20px;
  color: #333;
  margin-bottom: 5px;
  margin-top: 0;
}

.form-header p {
  font-size: 14px;
  color: #999;
  margin-bottom: 0;
}

.selected-mode-info {
  background: #fff3f0;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  border-left: 3px solid #ff5000;
}

.selected-mode-info strong {
  color: #ff5000;
}

/* 嵌套列表样式（用于租赁模式介绍中的阶梯定价等） */
.advantage-item ul ul {
  list-style: disc;
  margin-top: 8px;
  padding-left: 30px;
  margin-bottom: 8px;
}

.advantage-item ul ul li {
  padding-left: 0;
  position: static;
}

.advantage-item ul ul li:before {
  display: none;
}

/* 地址列表样式 */
.address-list {
  width: 100%;
}

.address-item {
  border: 2px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s;
  background: #fff;
}

.address-item:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.address-item-selected {
  border-color: #1890ff;
  background: #f0f7ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
}

.address-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.address-item-title {
  display: flex;
  align-items: center;
  flex: 1;
}

.address-text {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.address-item-check {
  color: #1890ff;
  font-size: 20px;
}

.address-item-info {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.address-info-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.address-info-label {
  color: #666;
  margin-right: 4px;
}

.address-info-value {
  color: #333;
  font-weight: 500;
}

.address-empty {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .product-main {
    flex-direction: column;
  }

  .product-left-section {
    width: 100%;
  }

  .product-image-box {
    width: 100%;
  }

  .product-info-box {
    position: relative;
    top: 0;
    width: 100%;
    max-height: none;
  }

  .main-image {
    min-height: 300px;
  }

  .spec-options {
    flex-direction: column;
  }

  .spec-option {
    width: 100%;
    min-width: auto;
  }
}

@media (max-width: 768px) {
  .product-main {
    gap: 15px;
  }

  .product-image-box {
    margin-bottom: 0;
  }

  .main-image {
    min-height: 250px;
    padding: 15px;
  }

  .product-title {
    font-size: 18px;
  }

  .price-main {
    font-size: 28px;
  }

  .service-tags {
    flex-wrap: wrap;
    gap: 10px;
  }

  .tab-header {
    overflow-x: auto;
    padding: 0 15px;
  }

  .tab-content {
    padding: 20px 15px;
  }
}
</style>

<script setup>
import {computed, onMounted, reactive, ref, watch} from 'vue'
import apiClient from "@/components/plugins/axios"
import {CircleCheck, Picture} from '@element-plus/icons-vue'
import {ElMessage} from 'element-plus'

import UploadMedia from "@/components/templates/UploadMedia.vue"
import {useRoute, useRouter} from 'vue-router'

const router = useRouter()
const route = useRoute()
const currentStep = ref(0)
const steps = ref([
  {title: '选择产品'},
  {title: '填写相关信息'}
])


// 第二步租赁相关状态
const rentalModeList = ref([]) // 租赁模式列表
const selectedRentalMode = ref(null) // 选中的主模式
const mainPrice = ref('--')
const priceUnit = ref('/月')
const priceDesc = ref('请选择租赁模式')
const activeTab = ref('intro')
const showFormModal = ref(false)
const rentalFormRef = ref()
const isSubmittingRental = ref(false)
const isRecognizingIdCard = ref(false) // 是否正在识别身份证
const rentalFormData = reactive({
  machine_model_id: null, // 产品型号ID
  selected_address_id: null, // 选中的地址ID
  apply_user_phone: '', // 手机号
  apply_contact_name: '', // 联系人名称
  user_receive_address: '', // 收货地址
  user_receive_lng_lat: null, // 收货经纬度
  estimate_time_num: 1, // 预估时间数
  is_deposit_free: false, // 是否免押金
  rental_purpose: '', // 用途说明
  legal_id_number: '', // 法人身份证号码
  legal_id_name: '', // 法人身份证名称
  legal_id_front_img: null, // 身份证正面URL
  legal_id_back_img: null, // 身份证反面URL
  // 用于文件上传的临时列表
  legalIdFrontList: [],
  legalIdBackList: []
})

// 租赁表单的地址列表
const rentalAddressList = ref([])

// 扁平化租赁模式列表：如果有子模式，直接展示子模式；否则展示主模式
const flattenedRentalModeList = computed(() => {
  const result = []
  rentalModeList.value.forEach(mode => {
    // 如果有子模式，直接展示子模式
    if (mode.child_modes && mode.child_modes.length > 0) {
      mode.child_modes.forEach(subMode => {
        result.push(subMode)
      })
    } else {
      // 没有子模式，展示主模式
      result.push(mode)
    }
  })
  return result
})

const selectedModeText = computed(() => {
  if (!selectedRentalMode.value) return ''
  return selectedRentalMode.value.mode_name
})

// 是否可以提交租赁申请
const canSubmitRental = computed(() => {
  return !!selectedRentalMode.value
})

// 获取租赁模式列表
async function getRentalModeList() {
  try {
    const res = await apiClient.get('/api/v1/rental/modes')
    if (res.data.code === 200) {
      rentalModeList.value = res.data.data || []
    }
  } catch (error) {
    ElMessage.error('获取租赁模式失败')
    console.error(error)
  }
}

// 验证联系电话格式（支持手机和座机号）
const validatePhone = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入联系电话'))
    return
  }
  const phone = value.trim()
  // 手机：11位数字，1开头
  const mobilePattern = /^1[3-9]\d{9}$/
  // 座机号：区号（3-4位）- 号码（7-8位），如 010-12345678 或 021-12345678
  const landlinePattern = /^0\d{2,3}-?\d{7,8}$/

  if (mobilePattern.test(phone) || landlinePattern.test(phone)) {
    callback()
  } else {
    callback(new Error('请输入正确的联系电话'))
  }
}

// 验证身份证号码
const validateIdNumber = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请上传身份证正面照片以自动识别'))
    return
  }
  // 18位身份证号码验证
  const idPattern = /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/
  if (idPattern.test(value)) {
    callback()
  } else {
    callback(new Error('身份证识别失败，请重新上传身份证正面照片'))
  }
}

// 处理租赁表单地址选择
function handleRentalAddressSelect(address) {
  rentalFormData.selected_address_id = address.id
  rentalFormData.apply_user_phone = address.telephone || ''
  rentalFormData.apply_contact_name = address.contact_name || ''
  rentalFormData.user_receive_address = `${address.detail_address}${address.building_number || ''}`
  rentalFormData.user_receive_lng_lat = address.location || null
}

// 获取租赁表单地址列表
async function getRentalAddressList() {
  try {
    const res = await apiClient.get('/api/v1/address/')
    if (res.data.code === 200) {
      rentalAddressList.value = res.data.data || []
      // 如果有默认地址，自动选择
      const defaultAddress = rentalAddressList.value.find(addr => addr.is_default)
      if (defaultAddress) {
        handleRentalAddressSelect(defaultAddress)
      } else if (rentalAddressList.value.length > 0) {
        // 没有默认地址，选择第一个
        handleRentalAddressSelect(rentalAddressList.value[0])
      }
    }
  } catch (error) {
    ElMessage.error('地址加载失败')
    console.error(error)
  }
}

// 租赁表单验证规则
const rentalFormRules = reactive({
  selected_address_id: [
    {required: true, message: '请选择收货地址', trigger: 'change'}
  ],
  legal_id_number: [
    {required: true, message: '请输入法人身份证号码', trigger: 'blur'},
    {validator: validateIdNumber, trigger: 'blur'}
  ],
  legal_id_name: [
    {required: true, message: '请上传身份证正面照片以自动识别', trigger: 'blur'}
  ],
  legal_id_front_img: [
    {required: true, message: '请上传身份证正面', trigger: 'change'}
  ],
  legal_id_back_img: [
    {required: true, message: '请上传身份证反面', trigger: 'change'}
  ]
})
const formData = reactive({
  productCategory: '', // 一级：产品分类
  serviceCategory: '', // 二级：产品类型
  service_device: '', // 三级：产品名称
})


// 产品列表
const productList = ref([])


async function getProducts() {
  const res = await apiClient.get(`/api/v1/device/category_model/rental`)
  if (res.data.code === 200) {
    productList.value = res.data.data || []
    // 自动选择第一个产品
    if (productList.value.length > 0) {
      selectProduct(productList.value[0])
    }
  }
}

// 选择产品
function selectProduct(product) {
  formData.service_device = product
  // 自动设置产品型号ID到租赁表单数据
  rentalFormData.machine_model_id = product.model_id
}


getProducts()


// 步骤控制
function nextStep() {
  if (currentStep.value === 0) {
    // 第一步：图形化选择产品，只需要检查是否选择了产品
    if (!formData.service_device) {
      ElMessage.warning('请先选择产品')
      return
    }
    currentStep.value++
  }
}

// 选择租赁模式
function selectRentalMode(mode) {
  selectedRentalMode.value = mode
  updatePriceDisplay(mode)
}

// 更新价格显示
function updatePriceDisplay(mode) {
  const priceInfo = getModePriceInfo(mode)
  mainPrice.value = priceInfo.price
  priceUnit.value = priceInfo.unit
  priceDesc.value = priceInfo.desc
}

// 获取模式价格信息
function getModePriceInfo(mode) {
  if (!mode) {
    return {price: '--', unit: '', desc: '请选择租赁模式'}
  }

  if (!mode.pricing_rules) {
    return {price: '--', unit: '', desc: mode.description || '请选择租赁模式'}
  }

  const rules = mode.pricing_rules

  // MODE_A 子模式
  if (mode.mode_code === 'MODE_A1' || mode.mode_code === 'MODE_A2') {
    const deviceRental = rules.DEVICE_RENTAL?.[0] || rules.PACKAGE_BUNDLE?.[0]
    if (deviceRental) {
      return {
        price: `¥${deviceRental.base_price}`,
        unit: deviceRental.price_unit,
        desc: mode.description || mode.mode_name
      }
    }
  }

  // MODE_B 按量计费
  if (mode.mode_code === 'MODE_B') {
    const tieredPricing = rules.TIERED_PRICING?.find(r => r.sub_rule_type === 'EXCESS_PORTION')
    if (tieredPricing) {
      const threshold = getModeThreshold(mode)
      return {
        price: `¥${tieredPricing.base_price}`,
        unit: tieredPricing.price_unit,
        desc: `按量计费 - ${threshold}`
      }
    }
  }

  // MODE_C 临时租赁
  if (mode.mode_code === 'MODE_C') {
    const deviceRental = rules.DEVICE_RENTAL?.[0]
    if (deviceRental) {
      const cardPrice = getCardPrintPrice(mode)
      return {
        price: `¥${deviceRental.base_price}`,
        unit: deviceRental.price_unit,
        desc: `临时租赁 - ${cardPrice}`
      }
    }
  }

  return {price: '--', unit: '', desc: mode.description || '请选择租赁模式'}
}

// 获取模式价格（用于显示）
function getModePrice(mode) {
  if (!mode) return null
  const priceInfo = getModePriceInfo(mode)
  return priceInfo.price !== '--' ? `${priceInfo.price}${priceInfo.unit}` : null
}

// 获取模式起付门槛（MODE_B）
function getModeThreshold(mode) {
  if (mode.mode_code !== 'MODE_B' || !mode.pricing_rules) return ''

  const deviceRental = mode.pricing_rules.DEVICE_RENTAL || []
  const quantity = deviceRental.find(r => r.sub_rule_type === 'MIN_PAY_QUANTITY')
  const amount = deviceRental.find(r => r.sub_rule_type === 'PAYMENT_AMOUNT')

  if (quantity && amount) {
    return `${quantity.base_price}张/¥${amount.base_price}`
  }
  return ''
}

// 获取制卡价格（MODE_C）
function getCardPrintPrice(mode) {
  if (mode.mode_code !== 'MODE_C' || !mode.pricing_rules) return ''

  const cardPrint = mode.pricing_rules.CARD_PRINT?.[0]
  if (cardPrint) {
    return `制卡${cardPrint.base_price}${cardPrint.price_unit}`
  }
  return ''
}

// 打开表单弹窗
async function openForm() {
  if (!selectedRentalMode.value) {
    ElMessage.warning('请先选择租赁模式！')
    return
  }

  // 打开弹窗前加载地址列表
  await getRentalAddressList()
  showFormModal.value = true
}

// 关闭表单弹窗
function closeForm() {
  showFormModal.value = false
}

// 监听身份证正面上传变化，自动更新URL
watch(() => rentalFormData.legalIdFrontList, (newList) => {
  if (newList && newList.length > 0) {
    rentalFormData.legal_id_front_img = newList[0].url
  } else {
    rentalFormData.legal_id_front_img = null
  }
}, {deep: true})

// 监听身份证反面上传变化，自动更新URL
watch(() => rentalFormData.legalIdBackList, (newList) => {
  if (newList && newList.length > 0) {
    rentalFormData.legal_id_back_img = newList[0].url
  } else {
    rentalFormData.legal_id_back_img = null
  }
}, {deep: true})

// 识别身份证信息
async function recognizeIdCard() {
  if (!rentalFormData.legal_id_front_img) {
    ElMessage.warning('请先上传身份证正面照片')
    return false
  }

  isRecognizingIdCard.value = true
  try {
    const res = await apiClient.post('/api/v1/common/id_card_info', {
      face_img_url: rentalFormData.legal_id_front_img
    })

    if (res.data.code === 200 && res.data.data) {
      // 自动填入识别结果
      rentalFormData.legal_id_name = res.data.data.name || ''
      rentalFormData.legal_id_number = res.data.data.id_card_number || ''
      return true
    } else {
      ElMessage.error(res.data.message || '身份证识别失败，请稍后重试')
      return false
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '身份证识别失败，请稍后重试')
    console.error('身份证识别失败:', error)
    return false
  } finally {
    isRecognizingIdCard.value = false
  }
}

// 提交租赁表单
async function submitRentalForm() {
  if (!rentalFormRef.value) {
    return
  }

  const valid = await new Promise(resolve => {
    rentalFormRef.value.validate(ok => resolve(ok))
  })

  if (!valid) {
    return
  }

  // 验证必填的图片
  if (!rentalFormData.legal_id_front_img) {
    ElMessage.warning('请上传身份证正面')
    return
  }

  if (!rentalFormData.legal_id_back_img) {
    ElMessage.warning('请上传身份证反面')
    return
  }

  // 先识别身份证信息
  const recognizeSuccess = await recognizeIdCard()
  if (!recognizeSuccess) {
    return
  }

  // 验证识别结果是否完整
  if (!rentalFormData.legal_id_name || !rentalFormData.legal_id_number) {
    ElMessage.warning('身份证信息识别不完整，请重新上传身份证正面照片')
    return
  }

  isSubmittingRental.value = true

  try {
    // 准备提交数据，按照接口参数格式
    const submitData = {
      machine_model_id: rentalFormData.machine_model_id,
      apply_user_phone: rentalFormData.apply_user_phone,
      apply_contact_name: rentalFormData.apply_contact_name,
      user_receive_address: rentalFormData.user_receive_address,
      user_receive_lng_lat: rentalFormData.user_receive_lng_lat,
      apply_rental_mode_id: selectedRentalMode.value.id,
      estimate_time_num: rentalFormData.estimate_time_num,
      is_deposit_free: rentalFormData.is_deposit_free,
      rental_purpose: rentalFormData.rental_purpose || null,
      legal_id_number: rentalFormData.legal_id_number,
      legal_id_name: rentalFormData.legal_id_name,
      legal_id_front_img: rentalFormData.legal_id_front_img,
      legal_id_back_img: rentalFormData.legal_id_back_img
    }

    // 调用API提交表单
    const res = await apiClient.post('/api/v1/rental/client/submit/application', submitData)
    if (res.data.code === 200) {
      ElMessage.success({
        message: '申请提交成功！我们会在1-2个工作日内与您联系，感谢您的申请！',
        duration: 3000
      })
      closeForm()
      // 重置表单
      rentalFormData.machine_model_id = null
      rentalFormData.selected_address_id = null
      rentalFormData.apply_user_phone = ''
      rentalFormData.apply_contact_name = ''
      rentalFormData.user_receive_address = ''
      rentalFormData.user_receive_lng_lat = null
      rentalFormData.estimate_time_num = 1
      rentalFormData.is_deposit_free = false
      rentalFormData.rental_purpose = ''
      rentalFormData.legal_id_number = ''
      rentalFormData.legal_id_name = ''
      rentalFormData.legal_id_front_img = null
      rentalFormData.legal_id_back_img = null
      rentalFormData.legalIdFrontList = []
      rentalFormData.legalIdBackList = []
      selectedRentalMode.value = null
      mainPrice.value = '--'
      priceUnit.value = '/月'
      priceDesc.value = '请选择租赁模式'
    } else {
      ElMessage.error(res.data.message || '提交失败，请稍后重试')
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '提交失败，请稍后重试')
    console.error(error)
  } finally {
    isSubmittingRental.value = false
  }
}

function prevStep() {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}


onMounted(() => {
  getRentalModeList()
})
</script>